import React, { useState, useRef, useEffect } from 'react';
import { LiaMapMarkerAltSolid,LiaEnvelope,LiaClock,  LiaInstagram, LiaPhoneAltSolid } from "react-icons/lia";
import { FaFacebook } from "react-icons/fa";
import { useLanguage } from '../context/LanguageContext';


const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const messageRef = useRef(null);

  // Scroll to message when status changes
  useEffect(() => {
    if (formStatus && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formStatus]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: '',
      });
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = t('contact.nameRequired');
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = t('contact.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.emailInvalid');
    }

    if (!formData.phone || formData.phone.trim() === '') {
      newErrors.phone = t('contact.phoneRequired');
    }

    if (!formData.subject || formData.subject.trim() === '') {
      newErrors.subject = t('contact.subjectRequired');
    }

    if (!formData.message || formData.message.trim() === '') {
      newErrors.message = t('contact.messageRequired');
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // If validation passes, show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    setFormStatus('');

    try {
      const response = await fetch('https://mailapi.janamarrakech.com/api/contact', {
      // const response = await fetch('http://localhost:3001/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormStatus('success');
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
          });
          setFormStatus('');
        }, 3000);
      } else {
        setFormStatus('error');
        console.error('Error:', data.errors || data.message);
      }
    } catch (error) {
      setFormStatus('error');
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSend = () => {
    setShowConfirmModal(false);
  };

  return (
    <div className="contact">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center parallax overlay"
        style={{
          backgroundImage: 'url(/images/DSC08406.webp)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-elegant font-bold mb-4" data-aos="fade-up">
            {t('contact.title')}
          </h1>
          <p className="text-xl md:text-2xl" data-aos="fade-up" data-aos-delay="100">
            {t('contact.reserveOrAsk')}
          </p>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div data-aos="fade-right">
              <h2 className="text-4xl font-elegant font-bold text-marrakech-dark mb-8">
                {t('contact.contactInfo')}
              </h2>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start">
                  <div className="bg-[#72201e] text-[#eee1ca] p-4 rounded-full mr-4 flex-shrink-0">
                    <LiaMapMarkerAltSolid className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#72201e] mb-2">{t('contact.address')}</h3>
                    <p className="text-black">
                      Zone Touristique De L, Bd Mohamed VI, Marrakech 40000
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start">
                  <div className="bg-[#72201e] text-[#eee1ca] p-4 rounded-full mr-4 flex-shrink-0">
                    <LiaPhoneAltSolid className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#72201e] mb-2">{t('contact.phone')}</h3>
                    <a
                      href="tel:+212644005467"
                      className="text-black hover:text-[#72201e] transition text-lg"
                    >
                      +2126 44 00 54 67
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start">
                  <div className="bg-[#72201e] text-[#eee1ca] p-4 rounded-full mr-4 flex-shrink-0">
                    <LiaEnvelope className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#72201e] mb-2">{t('contact.email')}</h3>
                    <a
                      href="mailto:reservation@janamarrakech.com"
                      className="text-black hover:text-[#72201e] transition text-lg"
                    >
                      reservation@janamarrakech.com
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start">
                  <div className="bg-[#72201e] text-[#eee1ca] p-4 rounded-full mr-4 flex-shrink-0">
                    <LiaClock className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#72201e] mb-2">{t('contact.hours')}</h3>
                    <p className="text-black">
                      <strong>{t('contact.mondayFriday')}</strong> 18H00 - 01H00<br />
                      <strong>{t('contact.weekend')}</strong> 18H00 - 03H00
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-[#72201e] mb-4">{t('contact.followUs')}</h3>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/Yamakanmarrakech/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#72201e] text-[#eee1ca] p-1 rounded-full  transition transform hover:scale-110"
                    aria-label="Facebook"
                  >
                    <FaFacebook className="text-4xl" />
                  </a>
                  <a
                    href="https://www.instagram.com/janamarrakech"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#72201e] text-[#eee1ca] p-1 rounded-full  transition transform hover:scale-110"
                    aria-label="Instagram"
                  >
                    <LiaInstagram className="text-4xl" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div data-aos="fade-left">
              <div className="bg-[#eee1ca] p-8 rounded-lg shadow-lg">
                <h2 className="text-3xl font-elegant font-bold text-[#72201e] mb-6">
                  {t('contact.sendUsMessage')}
                </h2>

                {formStatus === 'success' && (
                  <div ref={messageRef} className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {t('contact.successMessage')}
                  </div>
                )}

                {formStatus === 'error' && (
                  <div ref={messageRef} className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {t('contact.errorMessage')}
                  </div>
                )}

                {Object.values(errors).some(error => error !== "") && (
                  <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-6">
                    <h4 className="text-red-700 font-bold mb-2">⚠️ {t('contact.validationErrors')}</h4>
                    <ul className="list-disc list-inside text-red-600 text-sm space-y-1">
                      {Object.values(errors).map((error, index) =>
                        error !== "" ? <li key={index}>{error}</li> : null
                      )}
                    </ul>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-[#72201e] font-semibold mb-2">
                      {t('contact.fullName')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none ${
                        errors.name
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#eee1ca] focus:border-[#72201e]'
                      }`}
                      placeholder={t('contact.fullName')}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-[#72201e] font-semibold mb-2">
                      {t('contact.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none ${
                        errors.email
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#eee1ca] focus:border-[#72201e]'
                      }`}
                      placeholder={t('contact.email')}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-[#72201e] font-semibold mb-2">
                      {t('contact.phone')} *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none ${
                        errors.phone
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#eee1ca] focus:border-[#72201e]'
                      }`}
                      placeholder={t('contact.phone')}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-[#72201e] font-semibold mb-2">
                      {t('contact.subject')} *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none ${
                        errors.subject
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#eee1ca] focus:border-[#72201e]'
                      }`}
                      placeholder={t('contact.subject')}
                    />
                    {errors.subject && (
                      <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-[#72201e] font-semibold mb-2">
                      {t('contact.message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none ${
                        errors.message
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#eee1ca] focus:border-[#72201e]'
                      }`}
                      placeholder={t('contact.message')}
                    ></textarea>
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {isLoading ? `${t('contact.sendButton')}...` : t('contact.sendButton')}
                  </button>

                  {/* <p className="text-sm text-black text-center">
                    {t('contact.sendMessage')}
                  </p> */}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md mx-4">
            <h3 className="text-2xl font-bold text-[#72201e] mb-4">
              {t('contact.confirmSend')}
            </h3>
            <p className="text-black mb-2">
              <strong>{t('contact.fullName')}:</strong> {formData.name}
            </p>
            <p className="text-black mb-2">
              <strong>{t('contact.email')}:</strong> {formData.email}
            </p>
            <p className="text-black mb-2">
              <strong>{t('contact.phone')}:</strong> {formData.phone}
            </p>
            <p className="text-black mb-4">
              <strong>{t('contact.subject')}:</strong> {formData.subject}
            </p>
            <p className="text-black mb-6 bg-[#eee1ca] p-3 rounded max-h-32 overflow-y-auto">
              <strong>{t('contact.message')}:</strong><br />
              {formData.message}
            </p>
            <p className="text-sm text-gray-600 mb-6">
              {t('contact.confirmSend')}?
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleCancelSend}
                className="flex-1 px-4 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400 transition font-semibold"
              >
                {t('contact.cancel')}
              </button>
              <button
                onClick={handleConfirmSend}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-[#72201e] text-[#eee1ca] rounded-lg hover:bg-[#5a1815] transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? `${t('contact.confirm')}...` : t('contact.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-4xl font-elegant font-bold text-marrakech-dark mb-4">
              {t('contact.findUs')}
            </h2>
            <p className="text-lg text-black">
              {t('contact.location')}
            </p>
          </div>

          <div data-aos="fade-up" className="rounded-lg overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1445.0070208231145!2d-7.990939704846396!3d31.591032307432624!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xdafef83f2c0c5e5%3A0xeb1dd06a106e741!2sJANA%20MOROCCAN%20RESTAURANT!5e1!3m2!1sen!2sma!4v1780741516447!5m2!1sen!2sma"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Jana Marrakech Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

